import {
  Alert,
  Button,
  createStyles,
  Divider,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import React from 'react';

import EventTriggerConfigForm from './EventTriggerConfigForm';
import {canSaveRoutine} from './routineFormValidation';
import {
  AllowedEventTrigger,
  EventTriggerConfig,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import TimeTriggerConfigForm from './TimeTriggerConfigForm';
import TriggerTypeSelector from './TriggerTypeSelector';
import {RoutineFormState} from './useRoutineForm';

interface RoutineFormProps {
  isEdit: boolean;
  formState: RoutineFormState;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onRemoveFileBlock: (index: number) => void;
  onTriggerTypeChange: (value: TriggerType) => void;
  onTriggerConfigChange: (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => void;
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onArchive?: () => void;
  currentStatus?: RoutineStatus;
  allowedEventTriggers: Array<AllowedEventTrigger>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  form: {
    overflowY: 'auto',
    height: '100%',
  },
}));

export default function RoutineForm({
  isEdit,
  formState,
  onNameChange,
  onDescriptionChange,
  onPromptChange,
  onRemoveFileBlock,
  onTriggerTypeChange,
  onTriggerConfigChange,
  isDirty,
  isSaving,
  error,
  onSave,
  onCancel,
  onPause,
  onResume,
  onArchive,
  currentStatus,
  allowedEventTriggers,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineFormProps): JSX.Element {
  const {classes} = useStyles();

  const isTimeBased = formState.triggerType === TriggerType.TIME_BASED;

  const canSave = canSaveRoutine(formState, isEdit, isDirty, isSaving);

  return (
    <div className={classes.form}>
      <Stack spacing="md">
        <Title order={4}>{isEdit ? 'Edit Routine' : 'Create new routine'}</Title>

        <TextInput
          label="Name"
          value={formState.name}
          onChange={(e) => {
            onNameChange(e.currentTarget.value);
          }}
        />

        <Textarea
          label="Description"
          description="A summary of what this routine does - this will not be sent along to the agent."
          placeholder="optional"
          minRows={2}
          value={formState.description}
          onChange={(e) => {
            onDescriptionChange(e.currentTarget.value);
          }}
        />

        <Textarea
          label="Instructions"
          description="The prompt that will be sent to the agent when this routine runs."
          minRows={4}
          value={formState.prompt}
          onChange={(e) => {
            onPromptChange(e.currentTarget.value);
          }}
        />

        <div>
          <Group spacing={4} align="center">
            <Text size="sm" weight={600}>
              Attachments
            </Text>
            <Button variant="ghost" size="xs" compact={true} disabled={true}>
              + Add
            </Button>
          </Group>
          {formState.fileBlocks.length > 0 ? (
            <Stack spacing={4} mt={4}>
              {formState.fileBlocks.map((file, index) => (
                <Group key={file.external_file_id} spacing="xs" noWrap={true}>
                  <Text size="xs" lineClamp={1} style={{flex: 1}}>
                    {file.filename}
                  </Text>
                  <Button
                    variant="ghost"
                    size="xs"
                    compact={true}
                    onClick={() => {
                      onRemoveFileBlock(index);
                    }}
                  >
                    Remove
                  </Button>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text size="xs" color="dimmed">
              Additional context for this routine to run and create artifacts
            </Text>
          )}
        </div>

        <Divider />

        <TriggerTypeSelector
          value={formState.triggerType}
          onChange={onTriggerTypeChange}
          timeBasedEnabled={timeBasedEnabled}
          eventBasedEnabled={eventBasedEnabled}
          disabled={isEdit}
        />

        {formState.triggerType != null &&
          (isTimeBased ? (
            <TimeTriggerConfigForm
              config={formState.triggerConfig as Partial<TimeTriggerConfig>}
              onChange={onTriggerConfigChange}
            />
          ) : (
            <EventTriggerConfigForm
              config={formState.triggerConfig as Partial<EventTriggerConfig>}
              onChange={onTriggerConfigChange}
              allowedTriggers={allowedEventTriggers}
            />
          ))}

        {error != null && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group position="apart" pt="md">
          <Group spacing="xs">
            {isEdit && onArchive && (
              <Button variant="warn" size="sm" onClick={onArchive} disabled={isSaving}>
                Archive
              </Button>
            )}
            {isEdit && currentStatus === RoutineStatus.ACTIVE && onPause && (
              <Button variant="secondary" size="sm" onClick={onPause} disabled={isSaving}>
                Pause
              </Button>
            )}
            {isEdit && currentStatus === RoutineStatus.PAUSED && onResume && (
              <Button variant="secondary" size="sm" onClick={onResume} disabled={isSaving}>
                Resume
              </Button>
            )}
          </Group>
          <Group spacing="xs">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={onSave} disabled={!canSave} loading={isSaving}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </div>
  );
}
